// ============================================================
// Download Progress Manager - Destiny Junior
// Pengurus kemajuan muat turun global (PDF / Excel / JSON)
// Fasa: 'generating' (laporan dijana) -> 'downloading' (% bait) -> 'done'/'error'
// ============================================================

let _listeners = [];
let _downloads = {};
let _counter = 0;

const _emit = () => {
  const list = Object.values(_downloads).sort((a, b) => a.startedAt - b.startedAt);
  _listeners.forEach((fn) => {
    try { fn(list); } catch (e) { /* noop */ }
  });
};

export const subscribeDownloads = (listener) => {
  _listeners.push(listener);
  // Emit initial state
  listener(Object.values(_downloads));
  return () => {
    _listeners = _listeners.filter((l) => l !== listener);
  };
};

export const startDownload = (label) => {
  _counter += 1;
  const id = `dl_${Date.now()}_${_counter}`;
  _downloads[id] = {
    id,
    label: label || 'Muat turun fail',
    phase: 'generating', // generating | downloading | done | error
    percent: 0,
    loadedBytes: 0,
    totalBytes: null,
    startedAt: Date.now(),
  };
  _emit();
  return id;
};

export const updateDownload = (id, data) => {
  if (!_downloads[id]) return;
  _downloads[id] = { ..._downloads[id], ...data };
  _emit();
};

export const finishDownload = (id, success = true, errorMsg = null) => {
  if (!_downloads[id]) return;
  _downloads[id] = {
    ..._downloads[id],
    phase: success ? 'done' : 'error',
    percent: success ? 100 : _downloads[id].percent,
    errorMsg,
  };
  _emit();
  // Auto-remove selepas beberapa saat
  setTimeout(() => {
    delete _downloads[id];
    _emit();
  }, success ? 4000 : 7000);
};

export const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Muat turun fail dengan bar kemajuan global.
 *
 * @param {Object} options
 * @param {string} options.label - Label dipaparkan pada bar kemajuan (cth: 'Laporan Aktiviti (PDF)')
 * @param {string} options.filename - Nama fail untuk disimpan
 * @param {Function} options.request - (onDownloadProgress) => Promise axios dengan responseType 'blob'
 * @param {string} [options.mimeType] - MIME type pilihan untuk Blob
 * @returns {Promise<any>} response axios
 */
export const downloadFileWithProgress = async ({ label, filename, request, mimeType }) => {
  const id = startDownload(label);
  try {
    const onDownloadProgress = (progressEvent) => {
      const loaded = progressEvent.loaded || 0;
      const total = progressEvent.total || progressEvent.event?.lengthComputable
        ? progressEvent.total
        : null;
      if (loaded > 0) {
        if (total && total > 0) {
          const percent = Math.min(99, Math.round((loaded / total) * 100));
          updateDownload(id, {
            phase: 'downloading',
            percent,
            loadedBytes: loaded,
            totalBytes: total,
          });
        } else {
          // Saiz tidak diketahui - tunjuk bait dimuat turun
          updateDownload(id, {
            phase: 'downloading',
            percent: null,
            loadedBytes: loaded,
            totalBytes: null,
          });
        }
      }
    };

    const response = await request(onDownloadProgress);

    // Simpan fail (filename boleh jadi string atau fungsi(response) => nama)
    const resolvedName = typeof filename === 'function' ? filename(response) : filename;
    const blobData = mimeType
      ? new Blob([response.data], { type: mimeType })
      : new Blob([response.data]);
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', resolvedName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    finishDownload(id, true);
    return response;
  } catch (error) {
    finishDownload(id, false, 'Gagal muat turun');
    throw error;
  }
};

/**
 * Versi untuk buka blob dalam tab baharu (cth: preview PDF) dengan progress.
 */
export const openBlobWithProgress = async ({ label, request, mimeType = 'application/pdf' }) => {
  const id = startDownload(label);
  try {
    const onDownloadProgress = (progressEvent) => {
      const loaded = progressEvent.loaded || 0;
      const total = progressEvent.total || null;
      if (loaded > 0) {
        updateDownload(id, {
          phase: 'downloading',
          percent: total ? Math.min(99, Math.round((loaded / total) * 100)) : null,
          loadedBytes: loaded,
          totalBytes: total,
        });
      }
    };
    const response = await request(onDownloadProgress);
    const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
    window.open(url, '_blank');
    finishDownload(id, true);
    return response;
  } catch (error) {
    finishDownload(id, false, 'Gagal muat turun');
    throw error;
  }
};
